import { useMemo, useState } from 'react';
import type { ICustomViewMapping, IMarketPositionNode } from 'src/types/EUMarkets.type';

type HierarchyNode = Pick<
  IMarketPositionNode,
  'id' | 'volumeTypeId' | 'nodeId' | 'sourceId' | 'parentNodeId' | 'parentSourceId' | 'volumeType'
> & { total?: number | null };

const nodeKey = (node: HierarchyNode) => `${node.id}:${node.volumeTypeId}`;
const identityKey = (node: HierarchyNode) => `${node.nodeId}:${node.sourceId}`;
const parentKey = (node: HierarchyNode) => `${node.parentNodeId}:${node.parentSourceId}`;
const UNSELECTED = { checked: false, indeterminate: false };

const useCustomViewSelection = (nodes: HierarchyNode[]) => {
  const [isCreating, setIsCreating] = useState(false);
  const [defaultSelected, setDefaultSelected] = useState(false);
  const [choices, setChoices] = useState<Map<string, boolean>>(new Map());

  const hierarchy = useMemo(() => {
    const visibleNodes = nodes.filter(
      (node) => !(node.volumeType === 'MANUAL' && node.total === null),
    );
    const selectableNodes = Array.from(
      new Map(visibleNodes.map((node) => [nodeKey(node), node])).values(),
    );
    const calculatedNodes = selectableNodes.filter((node) => node.volumeType === 'CALC');
    const identities = new Map(calculatedNodes.map((node) => [identityKey(node), node]));
    const owners = new Map(calculatedNodes.map((node) => [node.id, node]));
    const children = new Map<string, HierarchyNode[]>();
    const roots: HierarchyNode[] = [];
    selectableNodes.forEach((node) => {
      const parent =
        node.volumeType === 'CALC'
          ? identities.get(parentKey(node))
          : (owners.get(node.id) ?? identities.get(identityKey(node)));
      if (!parent) {
        roots.push(node);
        return;
      }
      const key = nodeKey(parent);
      const siblings = children.get(key) ?? [];
      siblings.push(node);
      children.set(key, siblings);
    });
    return { nodes: selectableNodes, roots, children };
  }, [nodes]);

  const selectionStates = useMemo(() => {
    const states = new Map<string, typeof UNSELECTED>();
    const visiting = new Set<string>();
    const visit = (node: HierarchyNode, inherited: boolean): typeof UNSELECTED => {
      const key = nodeKey(node);
      if (visiting.has(key)) {
        return UNSELECTED;
      }
      visiting.add(key);
      const selected = choices.get(key) ?? inherited;
      const children = hierarchy.children.get(key) ?? [];
      const childStates = children.map((child) => visit(child, selected));
      const checked =
        childStates.length > 0 ? childStates.every((child) => child.checked) : selected;
      const hasSelection = childStates.some((child) => child.checked || child.indeterminate);
      const state = { checked, indeterminate: !checked && hasSelection };
      states.set(key, state);
      visiting.delete(key);
      return state;
    };

    hierarchy.roots.forEach((node) => visit(node, defaultSelected));
    hierarchy.nodes.forEach((node) => {
      if (!states.has(nodeKey(node))) {
        visit(node, defaultSelected);
      }
    });
    return states;
  }, [hierarchy, choices, defaultSelected]);

  const getSelectionState = (node: HierarchyNode) =>
    selectionStates.get(nodeKey(node)) ?? UNSELECTED;
  const hasSelectedNodes = Array.from(selectionStates.values()).some((state) => state.checked);
  const selectedMappings: ICustomViewMapping[] = hierarchy.nodes
    .filter((node) => {
      const state = getSelectionState(node);
      return state.checked || state.indeterminate;
    })
    .map((node) => ({ masterDataId: node.id, volumeTypeId: node.volumeTypeId }));

  const toggleSelection = (node: HierarchyNode) => {
    const selected = !getSelectionState(node).checked;
    setChoices((previous) => {
      const next = new Map(previous);
      const visited = new Set<string>();
      const clearDescendants = (parent: HierarchyNode) => {
        const key = nodeKey(parent);
        if (visited.has(key)) {
          return;
        }
        visited.add(key);
        next.delete(key);
        const children = hierarchy.children.get(key) ?? [];
        children.forEach(clearDescendants);
      };
      clearDescendants(node);
      next.set(nodeKey(node), selected);
      return next;
    });
  };

  const startCreating = (selectAll = false) => {
    setChoices(new Map());
    setDefaultSelected(selectAll);
    setIsCreating(true);
  };

  const cancelCreating = () => {
    setChoices(new Map());
    setDefaultSelected(false);
    setIsCreating(false);
  };

  return {
    isCreating,
    startCreating,
    cancelCreating,
    getSelectionState,
    toggleSelection,
    hasSelectedNodes,
    selectedMappings,
  };
};

export default useCustomViewSelection;
